<template>
  <div class="navigation-title-section">
    <div class="navigation-title-section-content">
      Batch artwork update (sheet CSV)
    </div>
  </div>
  <div class="batch-update-page">
    <div class="intro-row mb-3">
      <p class="mb-0 intro-text">
        Upload a CSV from your artwork sheet, pick fields, run a dry run, review the table below, then apply.
      </p>
      <el-button type="primary" @click="instructionsVisible = true">
        How this works
      </el-button>
    </div>

    <el-dialog
      v-model="instructionsVisible"
      title="Batch artwork update — how it works"
      width="640px"
      destroy-on-close
    >
      <div class="instructions-body">
        <h4 class="instructions-heading">1. Export from Google Sheets</h4>
        <ol class="instructions-list">
          <li>Open the artwork spreadsheet (single source of truth).</li>
          <li>
            Go to
            <strong>File&nbsp;→&nbsp;Download&nbsp;→&nbsp;Comma-separated values (.csv)</strong>.
          </li>
          <li>
            Import <strong>that</strong> file here. Do not re-save the CSV in Excel if that changes encoding or delimiters.
          </li>
        </ol>

        <h4 class="instructions-heading">2. How rows match Ceres</h4>
        <p>
          Each row is matched using the <strong>Link</strong> column. The slug is taken from the URL path
          (<code>…/artworks/your-slug</code>). Rows with an empty link, an invalid link, or a slug that does not exist in Ceres are skipped.
        </p>

        <h4 class="instructions-heading">3. Choose what to update</h4>
        <p>
          Check only the artwork <strong>columns</strong> you want to change. Relation data (artist, techniques, tags, colors, etc.) is not updated by this tool yet.
          Value indicators only update the text blocks; icons and structure stay as in the database.
        </p>

        <h4 class="instructions-heading">4. Dry run → review → apply</h4>
        <ol class="instructions-list">
          <li>
            Click <strong>Generate report</strong>. The preview table lists every planned change and any blocking errors.
          </li>
          <li>If there are errors, fix the sheet, export CSV again, and run a new dry run.</li>
          <li>
            When there are no errors, review the table. Optionally use <strong>Download report</strong> to save a CSV copy for your records.
          </li>
          <li>
            Click <strong>Apply updates</strong> to write changes to Ceres. You must use the <strong>same file and field checkboxes</strong> as the last successful dry run
            (if you change either, you need to run <strong>Generate report</strong> again).
          </li>
        </ol>

        <h4 class="instructions-heading">5. Changing file or fields</h4>
        <p>
          If you pick a different CSV or change which fields are checked, run <strong>Generate report</strong> again before applying.
        </p>
      </div>
      <template #footer>
        <el-button type="primary" @click="instructionsVisible = false">
          Close
        </el-button>
      </template>
    </el-dialog>

    <div class="mb-3">
      <label class="d-block mb-1">CSV file</label>
      <input
        type="file"
        accept=".csv,text/csv"
        @change="onFileChange"
      />
    </div>

    <div class="mb-3">
      <div class="mb-1">Fields to update</div>
      <!-- Available / out_of_stock: off for now; uncomment in CeresBE ArtworkBatchSheetService::ALLOWED_FIELD_KEYS to expose again. -->
      <el-checkbox-group
        v-model="selectedFields"
        @change="onSelectionChange"
        class="field-checkbox-group"
      >
        <el-checkbox
          v-for="opt in fieldOptions"
          :key="opt.key"
          :label="opt.key"
          border
        >
          {{ opt.label }}
        </el-checkbox>
      </el-checkbox-group>
    </div>

    <div class="actions mb-3">
      <el-tooltip
        :content="dryRunDisabledHint"
        placement="top"
        :disabled="canDryRun"
      >
        <span class="action-btn-wrap">
          <el-button
            type="success"
            class="batch-btn-generate"
            :disabled="!canDryRun"
            :loading="dryRunLoading"
            @click="runDryRun"
          >
            Generate report
          </el-button>
        </span>
      </el-tooltip>
      <el-tooltip
        :content="downloadReportDisabledHint"
        placement="top"
        :disabled="canDownloadReport"
      >
        <span class="action-btn-wrap">
          <el-button
            :disabled="!canDownloadReport"
            @click="downloadReport"
          >
            Download report
          </el-button>
        </span>
      </el-tooltip>
      <el-tooltip
        :content="applyDisabledHint"
        placement="top"
        :disabled="canApply"
      >
        <span class="action-btn-wrap">
          <el-button
            type="success"
            class="batch-btn-apply"
            :disabled="!canApply"
            :loading="applyLoading"
            @click="runApply"
          >
            Apply updates
          </el-button>
        </span>
      </el-tooltip>
    </div>

    <el-alert
      v-if="summary && summary.error_count > 0"
      type="error"
      :closable="false"
      class="mb-2"
      show-icon
      title="Fix CSV errors before applying"
      :description="`This report has ${summary.error_count} error(s). Fix the sheet, export a new CSV, and run Generate report again.`"
    />

    <div v-if="reportRows.length" class="report-preview mt-2">
      <h3 class="h6">Report preview ({{ reportRows.length }} rows)</h3>
      <div class="table-scroll">
        <table class="table table-sm batch-report-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Line</th>
              <th>Slug</th>
              <th>Artwork ID</th>
              <th>Field</th>
              <th>Old value</th>
              <th>New value</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in reportRows" :key="idx">
              <td>{{ row.type }}</td>
              <td>{{ row.line }}</td>
              <td>{{ row.slug }}</td>
              <td>{{ row.artwork_id }}</td>
              <td>{{ row.field }}</td>
              <td class="cell-long">{{ row.old_value }}</td>
              <td class="cell-long">{{ row.new_value }}</td>
              <td class="cell-long">{{ row.message }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ElNotification } from "element-plus";

function escapeCsvCell(value) {
  const s = value == null ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

export default {
  name: "ArtworkBatchUpdate",
  data() {
    return {
      csvFile: null,
      fieldOptions: [],
      selectedFields: [],
      dryRunLoading: false,
      applyLoading: false,
      fingerprint: null,
      reportRows: [],
      summary: null,
      instructionsVisible: false,
    };
  },
  computed: {
    canDryRun() {
      return !!(this.csvFile && this.selectedFields.length);
    },
    canDownloadReport() {
      return !!(this.fingerprint && this.reportRows.length > 0);
    },
    canApply() {
      return (
        this.fingerprint &&
        this.summary &&
        this.summary.error_count === 0 &&
        this.csvFile &&
        this.selectedFields.length > 0
      );
    },
    dryRunDisabledHint() {
      if (this.canDryRun) {
        return "";
      }
      const parts = [];
      if (!this.csvFile) {
        parts.push("choose a CSV file");
      }
      if (!this.selectedFields.length) {
        parts.push("select at least one field to update");
      }
      const need = parts.length ? parts.join(" and ") : "complete the form";
      return `Do this first: ${need}.`;
    },
    downloadReportDisabledHint() {
      if (this.canDownloadReport) {
        return "";
      }
      if (!this.fingerprint) {
        return 'Run "Generate report" first and wait until it finishes.';
      }
      if (!this.reportRows.length) {
        return "Dry run returned no rows to export; adjust the CSV or fields and run again.";
      }
      return "";
    },
    applyDisabledHint() {
      if (this.canApply) {
        return "";
      }
      if (!this.fingerprint) {
        return 'Run "Generate report" first.';
      }
      if (this.summary && this.summary.error_count > 0) {
        return "Fix every error in the CSV, then run a new dry run before applying.";
      }
      if (!this.csvFile || !this.selectedFields.length) {
        return "Use the same CSV and field selection as your last dry run.";
      }
      return "Complete the steps above to enable apply.";
    },
  },
  mounted() {
    this.loadFields();
  },
  methods: {
    apiRoot() {
      return `${process.env.VUE_APP_SHARED_API}api/`;
    },
    async loadFields() {
      try {
        const res = await axios.get(
          `${this.apiRoot()}artworks/batch-sheet/fields`,
          {
            headers: {
              Authorization: `Bearer ${this.$cookies.get("bearerToken")}`,
            },
          }
        );
        this.fieldOptions = res.data.fields || [];
      } catch (e) {
        ElNotification({
          title: "Could not load field list",
          message: e?.response?.data?.message || e.message,
          type: "error",
        });
      }
    },
    onFileChange(ev) {
      const f = ev.target.files && ev.target.files[0];
      this.csvFile = f || null;
      this.bumpSelection();
    },
    onSelectionChange() {
      this.bumpSelection();
    },
    bumpSelection() {
      this.clearDryRun();
    },
    clearDryRun() {
      this.fingerprint = null;
      this.reportRows = [];
      this.summary = null;
    },
    async runDryRun() {
      if (!this.canDryRun) return;
      this.dryRunLoading = true;
      this.clearDryRun();
      try {
        const fd = new FormData();
        fd.append("csv_file", this.csvFile);
        this.selectedFields.forEach((k) => fd.append("fields[]", k));
        const res = await axios.post(
          `${this.apiRoot()}artworks/batch-sheet/dry-run`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${this.$cookies.get("bearerToken")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        this.fingerprint = res.data.fingerprint;
        this.summary = res.data.summary;
        const changes = res.data.changes || [];
        const errors = res.data.errors || [];
        this.reportRows = [...errors, ...changes];
        ElNotification({
          title: "Dry run ready",
          message: `Changes: ${this.summary.change_count}, errors: ${this.summary.error_count}`,
          type: this.summary.error_count ? "warning" : "success",
        });
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors &&
            JSON.stringify(e.response.data.errors)) ||
          e.message;
        ElNotification({
          title: "Dry run failed",
          message: msg,
          type: "error",
        });
      } finally {
        this.dryRunLoading = false;
      }
    },
    downloadReport() {
      if (!this.canDownloadReport) return;
      const header = [
        "type",
        "line",
        "slug",
        "artwork_id",
        "field",
        "old_value",
        "new_value",
        "message",
      ];
      const lines = [header.map(escapeCsvCell).join(",")];
      this.reportRows.forEach((r) => {
        lines.push(
          [
            r.type,
            r.line,
            r.slug,
            r.artwork_id,
            r.field,
            r.old_value,
            r.new_value,
            r.message,
          ]
            .map(escapeCsvCell)
            .join(",")
        );
      });
      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `artwork-batch-dry-run-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async runApply() {
      if (!this.canApply) return;
      this.applyLoading = true;
      try {
        const fd = new FormData();
        fd.append("csv_file", this.csvFile);
        this.selectedFields.forEach((k) => fd.append("fields[]", k));
        fd.append("fingerprint", this.fingerprint);
        const res = await axios.post(
          `${this.apiRoot()}artworks/batch-sheet/apply`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${this.$cookies.get("bearerToken")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        ElNotification({
          title: "Updates applied",
          message: `Artwork rows touched: ${res.data.updated_artwork_rows}`,
          type: "success",
        });
        this.clearDryRun();
      } catch (e) {
        const msg = e?.response?.data?.message || e.message;
        ElNotification({
          title: "Apply failed",
          message: msg,
          type: "error",
        });
      } finally {
        this.applyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.batch-update-page {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px 24px;
}

.intro-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.intro-text {
  flex: 1 1 220px;
  max-width: 42rem;
  color: #2c3e50;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
}

.instructions-body {
  font-size: 14px;
  line-height: 1.55;
  color: #2c3e50;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
}

.instructions-body p,
.instructions-body li {
  text-wrap: pretty;
}

.instructions-heading {
  margin: 1rem 0 0.5rem;
  font-size: 15px;
  font-weight: 600;
}

.instructions-heading:first-child {
  margin-top: 0;
}

.instructions-list {
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
}

.instructions-list li {
  margin-bottom: 0.35rem;
}

.instructions-body p {
  margin: 0 0 0.75rem;
}

.instructions-body code {
  font-size: 0.9em;
  background: #f0f2f5;
  padding: 0.1em 0.35em;
  border-radius: 3px;
}
.field-checkbox-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: flex-start;
}

.field-checkbox-group :deep(.el-checkbox.is-bordered) {
  margin: 0;
  padding: 6px 12px 6px 8px;
  height: auto;
}

.field-checkbox-group :deep(.el-checkbox.is-bordered + .el-checkbox.is-bordered) {
  margin-left: 0;
}
.actions .action-btn-wrap {
  display: inline-block;
  margin-right: 8px;
  margin-bottom: 8px;
  vertical-align: middle;
}

.actions .el-button {
  margin-right: 0;
  margin-bottom: 0;
}

/* Dry run: success shape, Ceres forest green (vs brighter Apply below). */
.batch-update-page :deep(.batch-btn-generate.el-button--success) {
  background-color: #1f3c2a;
  border-color: #1f3c2a;
  color: #fff;
}

.batch-update-page :deep(.batch-btn-generate.el-button--success:hover),
.batch-update-page :deep(.batch-btn-generate.el-button--success:focus) {
  background-color: #2d5c46;
  border-color: #2d5c46;
  color: #fff;
}

.batch-update-page :deep(.batch-btn-generate.el-button--success.is-disabled) {
  background-color: #1f3c2a;
  border-color: #1f3c2a;
  color: #fff;
  opacity: 0.55;
}

/* Apply: default Element success green. */
.batch-update-page :deep(.batch-btn-apply.el-button--success) {
  background-color: #67c23a;
  border-color: #67c23a;
  color: #fff;
}

.batch-update-page :deep(.batch-btn-apply.el-button--success:hover),
.batch-update-page :deep(.batch-btn-apply.el-button--success:focus) {
  background-color: #85ce61;
  border-color: #85ce61;
  color: #fff;
}

.batch-update-page :deep(.batch-btn-apply.el-button--success.is-disabled) {
  background-color: #67c23a;
  border-color: #67c23a;
  color: #fff;
  opacity: 0.55;
}
.table-scroll {
  overflow: auto;
  max-height: 420px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  box-sizing: border-box;
}

/* Full grid + side borders; top/bottom come from .table-scroll. Separate collapse keeps sticky thead clean. */
.table-scroll table.batch-report-table {
  margin-bottom: 0;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  border-top: none;
  border-bottom: none;
  box-sizing: border-box;
}

.table-scroll table.batch-report-table thead th,
.table-scroll table.batch-report-table tbody td {
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  box-sizing: border-box;
}

.table-scroll table.batch-report-table thead th:last-child,
.table-scroll table.batch-report-table tbody td:last-child {
  border-right: none;
}

.table-scroll table.batch-report-table tbody tr:last-child td {
  border-bottom: none;
}

.table-scroll table.batch-report-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f8f9fa;
  background-clip: padding-box;
  box-shadow: 0 1px 0 #dee2e6, 0 2px 4px rgba(0, 0, 0, 0.06);
  vertical-align: middle;
  white-space: nowrap;
  transform: translateZ(0);
}
.cell-long {
  max-width: 220px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}
</style>
